import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { CesionQueryParamsProps, getCesionesByFilter } from "api/Cesion.service"
import { useContactosById } from "api/Contacto.hook"
import { GetCesionByFiltersDtoProps } from "api/DTOs/GetCesionByFiltersDto"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import SecondaryValidacion from "features/Validaciones/components/SecondaryValidation"
import { TabValidaciones } from "features/Validaciones/components/TabValidaciones/TabValidaciones"
import AceptarCesionModal from "features/Validaciones/modals/AceptarCesion"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { ModalAceptarCesiones } from "./components/ModalAceptarCesiones"
import { CESIONES_COLUMNS } from "./constants/CesionesColumns"
import { CESIONES_DEFAULT_STATUS } from "./constants/CesionesDefaultStatus"
import FilterMenu from "./FilterMenu"

export const Cesiones = () => {
  const { t } = useTranslation()
  const { state, pathname } = useLocation()
  const { id } = useParams<{ id: string }>()

  const { contactoById } = useContactosById(id)

  const isContactoPage = pathname.includes(ROUTES.CONTACTOS)
  const [showModal, setShowModal] = useState<{
    show: boolean
    rowData: GetCesionByFiltersDtoProps | undefined
  }>({
    show: false,
    rowData: undefined,
  })

  const cesionesCancelToken = useRef(axios.CancelToken.source())
  const fetchCesiones = useCallback(
    () => (queryParams: CesionQueryParamsProps) => {
      cesionesCancelToken.current?.cancel()
      cesionesCancelToken.current = axios.CancelToken.source()
      return getCesionesByFilter(
        {
          ...queryParams,
          SortingCriteria: queryParams.SortingCriteria ?? "Estado%20ASC",
          Estados: queryParams.Estados ?? CESIONES_DEFAULT_STATUS,
          EmpresaId: contactoById?.empresaContactoId,
        },
        cesionesCancelToken.current.token
      )
    },
    [contactoById]
  )
  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchCesiones,
    })

  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })

  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef = useRef<DataTablePrimeReact<GetCesionByFiltersDtoProps[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const handleClose = () => {
    setShowModal({
      show: false,
      rowData: undefined,
    })
  }

  const handleModal = async (rowData: GetCesionByFiltersDtoProps) => {
    setShowModal({
      show: !showModal.show,
      rowData,
    })
  }

  return (
    <div
      className={`overflow-hidden tw-bg-white ${
        !isContactoPage ? "!tw-h-[calc(100vh-64px)]" : "tw-h-full"
      } tw-text-base tw-font-medium`}
    >
      {!isContactoPage && <TabValidaciones />}
      <div
        className={`overflow-hidden tw-bg-white ${
          isContactoPage ? "tw-h-full" : "tw-h-[calc(100%-60px)]"
        }`}
      >
        <ModalAceptarCesiones state={state} />
        <div className='!tw-px-4 sm:!tw-px-8 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-center tw-gap-2 tw-py-2.5 tw-shadow-3xl tw-rounded-t-xl'>
          <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
            {t("validaciones.cesiones.title")} (
            {isLoading ? (
              <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
            ) : (
              <span>{totalCount}</span>
            )}
            )
          </div>
          <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
            <FilterMenu
              serviceQuery={setServiceQuery}
              sortingCriteria={sortingCriteria}
              maxResultCount={pagination.itemsPerPage}
              skipCount={pagination.skipCount}
              resetPagination={handleResetPagination}
            />
          </div>
        </div>
        <div className='tw-flex tw-flex-col tw-h-[calc(100%-60px)] tw-relative'>
          <SecondaryValidacion section='cesiones'>
            <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
              <TableERP
                refTable={dtRef}
                columns={CESIONES_COLUMNS({ t, handleModal, isContactoPage })}
                className='tw-h-full'
                columnResizeMode='expand'
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
                removableSort
                value={items}
                showGridlines
                dataKey='id'
                lazy
                scrollable
                size='small'
                loading={isLoading}
                emptyMessage={t("primeReact.table.emptyMessage")}
              />
            </div>
            <PaginationERP
              className={PAGINATION_STYLES}
              totalRecords={totalCount}
              first={pagination.skipCount}
              rows={pagination.itemsPerPage}
              resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
              setPagination={setPagination}
              optionDropdown={[10, 20, 50, 100]}
              texts={{
                textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
                textCurrentPageReport: `${t("pagination.from")}`,
              }}
            />
          </SecondaryValidacion>
        </div>
        <AceptarCesionModal
          modal={showModal}
          fetchTableData={fetchTableData}
          close={handleClose}
        />{" "}
      </div>
    </div>
  )
}
